import { computed } from 'vue';
import { useStore } from 'vuex';
import type { Node, Edge } from '@/modules/map-editor';

/**
 * Composable for initializing element caches.
 * Handles reactive data from store and cache initialization.
 * Consumed exclusively by useElements.
 */
export function useElementsInitializer() {
  const store = useStore();

  // Reactive data from store (internal — not exposed)
  const layerCurrentID = computed(() => store.getters["layers/getCurrentLayerId"]);
  const nodes = computed(() => store.getters["nodes/getNodes"]);
  const edges = computed(() => store.getters["edges/getEdges"]);

  /**
   * Initialize all element caches — nodes first, then edges.
   */
  function initializeAllCaches(): void {
    nodes.value.forEach((node: Node) => {
      node.cacheInstanceSetDimensions();
      node.cache(layerCurrentID.value);
    });

    edges.value.forEach((edge: Edge) => {
      edge.cacheInstanceSetDimensions();
      edge.cache(layerCurrentID.value, null);
    });
  }

  /**
   * Populate nodes from store
   */
  async function populateNodes(): Promise<void> {
    await store.dispatch('nodes/populateNodes');
  }

  /**
   * Populate edges from store
   */
  async function populateEdges(): Promise<void> {
    await store.dispatch('edges/populateEdges');
  }

  /**
   * Populate node-related edges from store
   */
  async function populateNodeRelatedEdges(): Promise<void> {
    await store.dispatch('nodes/populateRelatedEdges');
  }

  return {
    initializeAllCaches,
    populateNodes,
    populateEdges,
    populateNodeRelatedEdges
  };
}